import { lazy, Suspense } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import SEO from '../components/SEO';
import Section from '../components/Section';
import ProjectCard from '../components/ProjectCard';
import Industries from '../components/Industries';
import FAQ, { faqItems } from '../components/FAQ';
import { projects } from '../data/projects';

const HeroScene = lazy(() => import('../components/HeroScene'));

const services = [
  {
    title: 'Custom Web Platforms',
    description:
      'Production-grade web applications built around your specific workflows — not bent into a template.',
    icon: (
      <path d="M4 6h16v12H4z M4 10h16 M9 6v12" />
    ),
  },
  {
    title: 'SaaS Products',
    description:
      'From validated idea to multi-tenant SaaS with billing, auth, and admin tooling that scales with you.',
    icon: (
      <path d="M3 12a9 9 0 1118 0 9 9 0 01-18 0z M3 12h18 M12 3a14 14 0 010 18 M12 3a14 14 0 000 18" />
    ),
  },
  {
    title: 'Internal Tools',
    description:
      'Operations consoles, dashboards, and back-office systems that replace the spreadsheet sprawl.',
    icon: (
      <path d="M4 4h6v6H4z M14 4h6v6h-6z M4 14h6v6H4z M14 14h6v6h-6z" />
    ),
  },
  {
    title: 'AI-Powered Features',
    description:
      'Practical AI integrated into real product workflows — assistants, search, automation, summarization.',
    icon: (
      <path d="M12 2v4 M12 18v4 M2 12h4 M18 12h4 M5 5l3 3 M16 16l3 3 M5 19l3-3 M16 8l3-3 M9 12a3 3 0 116 0 3 3 0 01-6 0z" />
    ),
  },
  {
    title: 'API Integrations with Other Systems',
    description:
      'Connect your platform to the systems you already run — ERPs, CRMs, payment gateways, accounting, internal services. We design the integration layer so data flows reliably end to end.',
    icon: (
      <path d="M10 13a5 5 0 007 0l3-3a5 5 0 00-7-7l-1 1 M14 11a5 5 0 00-7 0l-3 3a5 5 0 007 7l1-1" />
    ),
  },
  {
    title: 'Product Engineering Partnership',
    description:
      'Embedded engineering for teams without an in-house technical lead. We ship, you grow.',
    icon: (
      <path d="M12 2l9 4-9 4-9-4 9-4z M3 10l9 4 9-4 M3 14l9 4 9-4" />
    ),
  },
];

const process = [
  {
    step: '01',
    title: 'Discover',
    body: 'We dig into your real workflows, constraints, and the outcome that actually matters to your business.',
    duration: '1–2 weeks',
    deliverable: 'A shared understanding of the problem and a written brief.',
  },
  {
    step: '02',
    title: 'Design',
    body: 'A focused product spec and technical plan — small enough to stay honest, complete enough to commit to.',
    duration: '1–2 weeks',
    deliverable: 'Fixed-scope spec, key flows, and a fixed price for phase one.',
  },
  {
    step: '03',
    title: 'Build',
    body: 'Weekly demos, production-quality code from day one, and a working slice of the product in your hands fast.',
    duration: '3–10 weeks',
    deliverable: 'A live, usable product slice — not a prototype.',
  },
  {
    step: '04',
    title: 'Iterate',
    body: 'Ship, measure, refine. Software is never done — but it is always usable.',
    duration: 'Ongoing',
    deliverable: 'Continuous improvement against real usage signals.',
  },
];

const stats = [
  { value: '100%', label: 'Custom-built solutions' },
  { value: '7+', label: 'Years of engineering' },
  { value: '< 4 wks', label: 'To first production release' },
  { value: '24/7', label: 'Direct founder access' },
];

const professionalServiceJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'ProfessionalService',
  '@id': 'https://nexsollabs.com/#organization',
  name: 'Nexsol Labs',
  alternateName: ['Nexsol Labs Software Studio', 'Nexsol Labs Skopje'],
  description:
    'Custom software development studio in Skopje, North Macedonia. We build web platforms, SaaS products, e-commerce, ERP systems, internal tools, and AI-powered features for clients in Macedonia, the Balkans, and Europe.',
  url: 'https://nexsollabs.com',
  logo: 'https://nexsollabs.com/logo.png',
  image: 'https://nexsollabs.com/logo.png',
  email: 'sasocvetanoski@nexsollabs.com',
  founder: {
    '@type': 'Person',
    name: 'Sasho Cvetanoski',
    jobTitle: 'Founder & Lead Engineer',
  },
  address: {
    '@type': 'PostalAddress',
    addressLocality: 'Skopje',
    addressCountry: 'MK',
  },
  areaServed: [
    { '@type': 'Country', name: 'North Macedonia' },
    { '@type': 'Country', name: 'Bulgaria' },
    { '@type': 'Country', name: 'Serbia' },
    { '@type': 'Country', name: 'Croatia' },
    { '@type': 'Country', name: 'Bosnia and Herzegovina' },
    { '@type': 'Place', name: 'Balkans' },
    { '@type': 'Place', name: 'Europe' },
  ],
  knowsAbout: [
    'Custom Software Development',
    'SaaS Development',
    'Web Application Development',
    'E-commerce Development',
    'ERP Systems',
    'API Integrations',
    'AI Integration',
    'Laravel',
    'React',
    'TypeScript',
    'PHP',
  ],
  serviceType: [
    'Custom Software Development',
    'SaaS Development',
    'Web Application Development',
    'E-commerce Development',
    'API Integrations',
    'AI Integration',
    'DevOps',
    'SEO',
  ],
  sameAs: [
    'https://www.linkedin.com/in/sasho-cvetanoski/',
    'https://github.com/SashoCv',
    'https://github.com/SashoCv/nexsolLabs',
  ],
};

const faqPageJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqItems.map((item) => ({
    '@type': 'Question',
    name: item.q,
    acceptedAnswer: {
      '@type': 'Answer',
      text: item.a,
    },
  })),
};

export default function Home() {
  const featured = projects.slice(0, 3);

  return (
    <>
      <SEO
        title="Nexsol Labs — Custom Software Development in Skopje, North Macedonia"
        description="Custom software development studio in Skopje, North Macedonia. We build web platforms, SaaS products, e-commerce, ERP systems, and AI-powered tools for clients across Macedonia, the Balkans, and Europe."
        path="/"
        keywords="custom software development Skopje, software development North Macedonia, софтверска куќа Скопје, развој на софтвер, custom софтвер Македонија, SaaS development Macedonia, web development Skopje, Laravel React agency, AI integration, ERP system Macedonia, Nexsol Labs"
        jsonLd={[professionalServiceJsonLd, faqPageJsonLd]}
      />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-40" />
        <div className="absolute inset-0 bg-hero-gradient" />
        <div className="container-page relative grid items-center gap-10 py-20 md:grid-cols-2 md:py-28">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: 'easeOut' }}
          >
            <p className="eyebrow">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
              Custom software studio · Skopje, North Macedonia
            </p>
            <h1 className="mt-5 font-display text-4xl font-semibold leading-[1.05] tracking-tight text-white md:text-6xl">
              <span className="gradient-text">Custom software</span>{' '}
              engineered for the way{' '}
              <span className="text-accent-400">your team works.</span>
            </h1>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-white/70 md:text-lg">
              Nexsol Labs is a custom software studio based in Skopje. We build tailored
              web platforms, SaaS products, internal tools, and AI-powered systems for
              clients across North Macedonia, the Balkans, and Europe — no bloated
              templates, no off-the-shelf compromises.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/contact" className="btn-primary">
                Start a project
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  className="h-4 w-4"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 12h14M13 6l6 6-6 6" />
                </svg>
              </Link>
              <Link to="/projects" className="btn-ghost">
                See our work
              </Link>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.9, ease: 'easeOut', delay: 0.1 }}
            className="relative"
          >
            <div className="absolute inset-0 -z-10 rounded-3xl bg-accent-500/10 blur-3xl" />
            <Suspense
              fallback={
                <div className="h-[420px] w-full animate-pulse rounded-3xl border border-white/10 bg-white/[0.02] md:h-[540px]" />
              }
            >
              <HeroScene />
            </Suspense>
          </motion.div>
        </div>
      </section>

      {/* STATS */}
      <section className="container-page">
        <div className="grid grid-cols-2 gap-px overflow-hidden rounded-2xl border border-white/10 bg-white/5 md:grid-cols-4">
          {stats.map((s) => (
            <div
              key={s.label}
              className="bg-ink-950/80 px-6 py-6 text-center md:py-8"
            >
              <p className="font-display text-2xl font-semibold text-white md:text-3xl">
                {s.value}
              </p>
              <p className="mt-1 text-xs uppercase tracking-[0.16em] text-white/55">
                {s.label}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* SERVICES */}
      <Section
        eyebrow="What we build"
        title={
          <>
            Software designed around{' '}
            <span className="text-accent-400">your problem</span>, not ours.
          </>
        }
        description="We focus on custom solutions — built from first principles, owned by you, and shaped exactly to the workflow you actually run."
      >
        <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {services.map((s, i) => (
            <motion.div
              key={s.title}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-40px' }}
              transition={{ duration: 0.45, delay: i * 0.05 }}
              className="card"
            >
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-accent-500/20 to-violet-500/20 text-accent-400">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="h-5 w-5"
                >
                  {s.icon}
                </svg>
              </div>
              <h3 className="mt-5 font-display text-lg font-semibold text-white">
                {s.title}
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-white/65">
                {s.description}
              </p>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* PROCESS */}
      <Section
        eyebrow="How we work"
        title="A small team, an honest process, real software."
        description="We keep the team intentionally small so the people designing your product are the same ones shipping it. No hand-offs, no telephone game."
      >
        <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-4">
          {process.map((p, i) => (
            <motion.div
              key={p.step}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-40px' }}
              transition={{ duration: 0.45, delay: i * 0.06 }}
              className="card relative flex flex-col"
            >
              <p className="font-mono text-xs text-accent-400">{p.step}</p>
              <h3 className="mt-3 font-display text-lg font-semibold text-white">
                {p.title}
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-white/65">{p.body}</p>
              <div className="mt-5 border-t border-white/10 pt-4 text-xs">
                <p className="text-white/45">
                  <span className="text-white/70">Typical duration · </span>
                  {p.duration}
                </p>
                <p className="mt-1.5 text-white/45">
                  <span className="text-white/70">You leave with · </span>
                  {p.deliverable}
                </p>
              </div>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* FAQ */}
      <FAQ />

      {/* INDUSTRIES */}
      <Industries />

      {/* FEATURED PROJECTS */}
      <Section
        eyebrow="Selected work"
        title="A few of the things we've built."
        description="A small sample of recent projects. Industries change, the bar doesn't: durable, well-designed software the team actually wants to use."
      >
        <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {featured.map((p, i) => (
            <ProjectCard key={p.slug} project={p} index={i} />
          ))}
        </div>
        <div className="mt-10 flex justify-center">
          <Link to="/projects" className="btn-ghost">
            View all projects
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              className="h-4 w-4"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 12h14M13 6l6 6-6 6" />
            </svg>
          </Link>
        </div>
      </Section>

      {/* CTA */}
      <section className="container-page py-20 md:py-28">
        <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-accent-500/15 via-violet-500/10 to-transparent p-10 md:p-16">
          <div className="absolute inset-0 grid-bg opacity-30" />
          <div className="relative grid items-center gap-8 md:grid-cols-[1.4fr_1fr]">
            <div>
              <h2 className="font-display text-3xl font-semibold leading-tight tracking-tight text-white md:text-4xl">
                Have a software problem worth solving properly?
              </h2>
              <p className="mt-4 max-w-xl text-base text-white/70">
                Tell us what you're trying to build. We'll come back with an honest answer
                — what's possible, what it takes, and whether we're the right team to do it.
              </p>
            </div>
            <div className="flex flex-wrap gap-3 md:justify-end">
              <Link to="/contact" className="btn-primary">
                Start a conversation
              </Link>
              <Link to="/about" className="btn-ghost">
                Learn about us
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
